import Q from 'q';
import _ from 'lodash';

import AquaAudio from './AquaAudio'; // eslint-disable-line import/no-unresolved, import/extensions
import createAudioSafetyTimer from './audioSafetyTimer';
import { logRumAction } from './DDRum';

export const TaskType = {
  // playing of audio track:
  // starts with play() method of AquaAudio lib, ends with onend event
  AUDIO: 'audio',
  // playing of audio track from passed in track:
  // starts with play() method of AquaAudio lib, ends with onend event
  AUDIOTRACK: 'audiotrack',
  // React action:
  // starts when reducer action is dispatched, ends when component finishes re-rendering
  ACTION: 'action',
  // simple timeout:
  // starts with setTimeout(), ends when number of ms is over
  TIMEOUT: 'timeout',
  ANIMATION: 'animation',
};

export const SequenceStatuses = {
  // once sequence is created, it's inactive
  INACTIVE: 'inactive',
  // once sequence is run, it's active (in progress)
  ACTIVE: 'active',
  // once sequence is finished
  FINISHED: 'finished',
};

/* DEBUGGING FUNCTIONS */

// function logEvent(message) {
//   console.log(message);
// }

// function wrapDefer(task) {
//   task.deferred.resolve = _.wrap(task.deferred.resolve, (func) => {
//     if (task.terminateDelay !== undefined) {
//       logEvent(`SEQUENCE: ${task.type} task #${task.index} "${task.description}"
//         is terminated after delay ${task.terminateDelay}`);
//     } else {
//       logEvent(`SEQUENCE: ${task.type} task #${task.index} "${task.description}" is resolved`);
//     }

//     return func();
//   });
// }

class TaskSequence {
  constructor(tasksDefers) {
    this.batchId = Math.floor(Math.random() * 100000);

    // unordered list of tasks
    this.sequence = [];

    // object with lists of unresolved tasks defers, grouped by property key
    // coming from external component
    this.tasksDefers = tasksDefers;

    // initial value of order of execution is 0
    this.orderOfExecution = 0;

    // index of group of tasks being run at the current moment
    this.currentGroupIndex = 0;

    this.status = SequenceStatuses.INACTIVE;

    this.isStopOnend = false;
  }

  addParallelTask(task) {
    // parallel task is added into sequence with same order of execution, as previous task
    // meaning this task and every next parallel task will be run at the same time
    this.addTask(task, this.orderOfExecution);
  }

  addConsecutiveTask(task) {
    // consecutive task is added into sequence with increased order of execution (+1)
    // meaning this task will be run in the next group of execution
    this.addTask(task, ++this.orderOfExecution);
  }

  addTimeout(delay) {
    const deferred = Q.defer();

    // timeout task is added into sequence with increased order of execution (+1)
    this.orderOfExecution++;

    const taskObj = {
      type: TaskType.TIMEOUT,
      description: 'timeout',
      // once timeout task is added, order of execution needs to be increased again
      // to be sure no task is added with the same order of execution after this
      orderOfExecution: this.orderOfExecution++,
      deferred,
      run: deferred.resolve,
      delay,
      preventExecution: false,
    };

    taskObj.executionPreventerFunc = this.getTaskExecutionPreventerFunc(taskObj);

    this.sequence.push(taskObj);
  }

  addTask(task, orderOfExecution) {
    if (task.type === TaskType.AUDIOTRACK) {
      this.addAudioTrackTask(task, orderOfExecution);
    } else if (task.type === TaskType.ACTION) {
      this.addActionTask(task, orderOfExecution);
    } else if (task.type === TaskType.ANIMATION) {
      this.addAnimationTask(task, orderOfExecution);
    }
  }

  addActionTask(task, orderOfExecution) {
    const deferred = Q.defer();

    const taskObj = {
      type: TaskType.ACTION,
      description: task.description,
      orderOfExecution,
      deferred,
      run: task.start,
      delay: task.delay || 0,
      terminateDelay: task.terminateDelay,
      preventDelayedPlay: false,
      preventExecution: false,
    };

    // if there is no implicit description, construct it from property name
    if (task.prop && !task.description) {
      taskObj.description = `property ${task.prop}`;
    }

    if (this.tasksDefers && task.prop) {
      // if task is properly defined as "defer task", assign external resolver
      this.tasksDefers[task.prop] = this.tasksDefers[task.prop] || [];
      this.tasksDefers[task.prop].push(deferred);
    } else {
      // if no, will resolve it internally via setTimeout(, 0)
      taskObj.terminateDelay = 0;
    }

    taskObj.executionPreventerFunc = this.getTaskExecutionPreventerFunc(taskObj);

    this.sequence.push(taskObj);
  }

  addAnimationTask(task, orderOfExecution) {
    const deferred = Q.defer();

    const taskObj = {
      type: TaskType.ANIMATION,
      description: task.description,
      orderOfExecution,
      deferred,
      delay: task.delay || 0,
      preventDelayedPlay: false,
      preventExecution: false,
    };

    // if there is no implicit description, construct it from property name
    if (task.prop && !task.description) {
      taskObj.description = `animation ${task.prop}`;
    }

    // for animation task as a start method we receive start() method of Animated object
    // so we just pass into it defer resolve, Animated will call it once animation is finished
    taskObj.run = () => task.start(taskObj.deferred.resolve);

    taskObj.executionPreventerFunc = this.getTaskExecutionPreventerFunc(taskObj);

    this.sequence.push(taskObj);
  }

  addAudioTrackTask(task, orderOfExecution) {
    const deferred = Q.defer();

    const taskObj = {
      type: TaskType.AUDIOTRACK,
      description: task.audioDesc,
      orderOfExecution,
      deferred,
      delay: task.delay || 0,
      terminateDelay: task.terminateDelay,
      preventDelayedPlay: false,
      preventExecution: false,
      volume: task.volume !== undefined ? task.volume : 1,
      caption: task.caption || '',
    };

    let safetyNet = null;
    // a WebAudio load still in flight at stop() fires onload anyway, arming a net nothing can cancel
    let stopped = false;

    taskObj.run = () => {
      // stopBatch() also stops tasks yet to run, and an already-scheduled delayed one still starts
      stopped = false;
      const audioId = taskObj.caption || taskObj.description;
      const resolveTask = () => {
        safetyNet?.cancel();
        taskObj.track?.sound?.unload?.();
        taskObj.deferred.resolve();
      };

      taskObj.track = new AquaAudio(task.audioTrack, task.audioDesc, {
        caption: taskObj.caption,
        volume: taskObj.volume,
        // Safety timeout: if onend never fires (eg, iOS silently blocks play() on audio
        // that starts mid-sequence after swipe-back, outside a user gesture context),
        // the sequence would hang forever. Set a timeout of duration + 2s after load so that
        // any stuck audio auto-recovers without the student needing to tap.
        onLoadCallBack: () => {
          if (stopped) {
            return;
          }
          const durationMs = (taskObj.track.duration() * 1000) + 2000;
          safetyNet = createAudioSafetyTimer(durationMs, () => {
            logRumAction('audio safety timeout', { description: audioId });
            resolveTask();
          });
          safetyNet.arm();
        },
        onend: resolveTask,
        // If an audio track fails to load, continue the sequence rather than hanging indefinitely.
        // TODO clarify how to deal with issue:
        // if in group we have e.g. 1 audio track and 1 animation and audio track fails,
        // animation will be immediate, that's bad user experience
        // unload is required to return the audio node to the HTML5 pool.
        // onend never fires on a load error, so without this the node would stay checked out permanently.
        onerror: () => {
          console.warn(`TaskSequence audio load error: ${audioId}`);
          resolveTask();
        },
        // iOS can silently block play() (no error, no end) leaving the sequence hung.
        // unload is required - without it the audio node stays checked out from the HTML5 pool,
        // eventually depleting it and causing silent failures on future tasks.
        onplayerror: () => {
          console.warn(`TaskSequence audio play error: ${audioId}`);
          resolveTask();
        },
      });
      taskObj.track.play();
      if (task.startFromTime) {
        taskObj.track.seek(task.startFromTime);
      }
    };

    taskObj.stop = () => {
      stopped = true;
      safetyNet?.cancel();
      taskObj.track?.resetCaption?.();
      taskObj.track?.sound?.unload?.();
    };

    taskObj.preventDelayedPlayFunc = () => {
      taskObj.preventDelayedPlay = true;
    };

    taskObj.executionPreventerFunc = this.getTaskExecutionPreventerFunc(taskObj);

    this.sequence.push(taskObj);
  }

  getTaskExecutionPreventerFunc(taskObj) {
    return () => {
      // eslint-disable-next-line no-param-reassign
      taskObj.preventExecution = true;
    };
  }

  // stops audio playback
  stopPlayingAudioTrack() {
    this.sequence.forEach((element) => {
      if (element.type === 'audiotrack') {
        element.preventDelayedPlayFunc();
        element.stop();
        element.deferred.reject();
      }
    });
  }
  /* eslint-disable  */
  // stops all tasks in sequence
  stopBatch(isStopOnend = false) {
    this.isStopOnend = isStopOnend; // set flag to cancel onend along with batch
    this.sequence.forEach(task => {
      task.executionPreventerFunc();
      task.deferred.reject();
      if (task.type === TaskType.AUDIOTRACK || task.type === TaskType.AUDIO) {
        task.stop();
      }
    });
  }
  /* eslint-enable  */

  /* eslint-disable no-param-reassign */
  getCurrentGroup() {
    if (this.groupedSequence && this.groupedSequence[this.currentGroupIndex]) {
      return this.groupedSequence[this.currentGroupIndex].map((task, index) => {
        // ig group index is 0, let task indexes be 101, 102, 103
        task.index = this.currentGroupIndex + 1;
        task.index *= 100;
        task.index += index + 1;
        return task;
      });
    }
    return [];
  }
  /* eslint-enable no-param-reassign */

  runCurrentGroup() {
    // run all tasks in current group in a "parallel" mode
    // technically it's not parallel (it's JS), so "parallel" here means:
    // next group won't be started until ALL tasks from current group are finished
    this.getCurrentGroup().forEach((task) => {
      // if preventExecution flag was set, replace run() with empty function
      /* eslint-disable  */
      task.run = task.preventExecution ? () => { } : task.run;
      /* eslint-enable  */
      // if delay is defined - run with timeout, if no - immediately
      if (task.delay) {
        // logEvent(`SEQUENCE: ${task.type} task #${task.index} "${task.description}"
        //   is started with delay ${task.delay}`);

        if (task.type === 'audiotrack' || task.type === 'audio') {
          setTimeout(() => {
            if (!task.preventDelayedPlay) {
              task.run();
            }
          }, task.delay);
        } else {
          setTimeout(task.run, task.delay);
        }
      } else {
        // logEvent(`SEQUENCE: ${task.type} task #${task.index} "${task.description}" is started`);
        task.run();
      }

      if (task.terminateDelay !== undefined) {
        setTimeout(() => {
          task.deferred.resolve();
        }, task.terminateDelay);
      }
    });
  }

  getCurrentGroupPromises() {
    return this.getCurrentGroup().map(task => task.deferred.promise);
  }

  startBatch(config) {
    // logEvent(`SEQUENCE: batch #${this.batchId} started`);
    // will be called once all tasks are finished
    const onend = config ? config.onend : null;

    // this.sequence.forEach(wrapDefer);

    // here we group all tasks by order of execution into list of groups
    // those groups will be run one by one
    this.groupedSequence = Object.values(
      _.groupBy(this.sequence, 'orderOfExecution'),
    );

    this.status = SequenceStatuses.ACTIVE;

    if (!this.isStopOnend) {
      this.runTasksGroupsRecursive(onend);
    } else {
      this.status = SequenceStatuses.INACTIVE;
      this.isStopOnend = false;
    }
  }

  runTasksGroupsRecursive(onend = null) {
    // wait ALL tasks in current group to be finished (every defer is resolved)
    Q.all(this.getCurrentGroupPromises())
      .then(() => {
        // logEvent(`SEQUENCE: group #${this.currentGroupIndex + 1} finished`);
        // increase group index
        this.currentGroupIndex++;

        if (this.getCurrentGroup().length) {
          // if next group exists, we switch to it
          this.runTasksGroupsRecursive(onend);
        } else {
          // if no, finish execution
          this.finishBatch(onend);
        }
      })
      .catch(() => {
        // TODO decide how to deal with this
        // audio track will not fail in terms of promise, so it's timeout or reducer action -
        // ideally won't never happen
        // console.log(e);
        this.finishBatch(onend);
      });

    // run tasks in current group
    this.runCurrentGroup();
  }

  finishBatch(onend = null) {
    this.status = SequenceStatuses.FINISHED;
    // logEvent(`SEQUENCE: batch #${this.batchId} finished`);

    if (onend && !this.isStopOnend) {
      onend();
    } else {
      this.isStopOnend = false; // reset flag to default
    }
  }

  isSequenceInProgress() {
    return this.status === SequenceStatuses.ACTIVE;
  }

  isSequenceFinished() {
    return this.status === SequenceStatuses.FINISHED;
  }

  /*
    volume param can be in range between 0(muted) and 1(full volume).
  */
  setVolume(volume) {
    this.sequence.forEach(task =>
      task.track.setVolume(volume),
    );
  }
}

export default TaskSequence;
